import {
  dateTime as dateTimeBase,
  money as moneyBase,
  relative as relativeBase,
} from '@wyocspay/ui';

/**
 * Bulgarian formatting for the admin console.
 *
 * The console is single-language by decision, so the locale is bound here once
 * instead of being threaded through every call site. `shortId` needs no locale
 * and is imported straight from the shared package.
 */
const LOCALE = 'bg-BG';

export function dateTime(iso: string | null): string {
  return dateTimeBase(iso, LOCALE);
}

export function relative(iso: string | null): string {
  return relativeBase(iso, LOCALE);
}

/**
 * Amounts deliberately stay in Stripe's own rendering: `$7,104.00`, not
 * `7104,00 щ.д.`.
 *
 * This console is read side by side with the Stripe dashboard when something is
 * being investigated, and a figure that has to be mentally re-punctuated before
 * it can be compared is a figure that will eventually be misread. Dates and
 * relative times are localised because nothing is reconciled against them.
 */
export function money(amount: number, currency: string): string {
  return moneyBase(amount, currency);
}

/**
 * Bulgarian wording for the enum values the API returns.
 *
 * Anything unrecognised falls through unchanged rather than being blanked, so a
 * value added on the server appears as itself instead of disappearing from the
 * screen. Note that badges elsewhere still show the raw enum on purpose: those
 * are what you quote when talking to Stripe support.
 */
const STATUS: Record<string, [singular: string, plural: string]> = {
  ACTIVE: ['активен', 'активни'],
  PENDING: ['чакащ', 'чакащи'],
  SUSPENDED: ['спрян', 'спрени'],
  CLOSED: ['закрит', 'закрити'],
  REJECTED: ['отхвърлен', 'отхвърлени'],
  critical: ['критичен', 'критични'],
  warning: ['предупреждение', 'предупреждения'],
  info: ['информационен', 'информационни'],
};

/**
 * `count` is not optional by accident: Bulgarian agrees the adjective with the
 * number, so "1 активни" is simply wrong. Callers that have a count must pass
 * it; those that are labelling a column get the plural, which is what a header
 * wants anyway.
 */
export function statusLabel(value: string, count?: number): string {
  const forms = STATUS[value] ?? STATUS[value.toUpperCase()];
  if (!forms) return value.toLowerCase();
  return count === 1 ? forms[0] : forms[1];
}

/**
 * Badge wording.
 *
 * The raw enum stays reachable as the badge's tooltip and is what the audit log
 * and the API keep using; only what a person reads at a glance is translated.
 * An unmapped value falls through with its underscores opened out, so a state
 * added on the server is still legible rather than blank.
 */
const BADGE: Record<string, string> = {
  // merchant and user accounts
  ACTIVE: 'АКТИВЕН',
  PENDING: 'ЧАКА',
  SUSPENDED: 'СПРЯН',
  CLOSED: 'ЗАКРИТ',
  FROZEN: 'ЗАМРАЗЕН',
  // payments
  CREATED: 'СЪЗДАДЕНО',
  PROCESSING: 'ОБРАБОТВА СЕ',
  REQUIRES_ACTION: 'ЧАКА ДЕЙСТВИЕ',
  REQUIRES_PAYMENT_METHOD: 'ЧАКА КАРТА',
  SUCCEEDED: 'УСПЕШНО',
  FAILED: 'НЕУСПЕШНО',
  CANCELLED: 'ОТКАЗАНО',
  REFUNDED: 'РЕФУНДИРАНО',
  PARTIALLY_REFUNDED: 'ЧАСТИЧНО РЕФУНДИРАНО',
  DISPUTED: 'ОСПОРЕНО',
  EXPIRED: 'ИЗТЕКЪЛ',
  REVOKED: 'ОТНЕТ',
  // payment attempts
  INITIATED: 'ЗАПОЧНАТ',
  // approvals and requests
  APPROVED: 'ОДОБРЕНО',
  REJECTED: 'ОТХВЪРЛЕНО',
  DENIED: 'ОТКАЗАНО',
  SUBMITTED: 'ПОДАДЕНО',
  INFO_REQUESTED: 'ИСКАНА ИНФОРМАЦИЯ',
  WITHDRAWN: 'ОТТЕГЛЕНО',
  OPEN: 'ОТВОРЕН',
  COMPLETE: 'ЗАВЪРШЕНО',
  // capabilities
  ENABLED: 'ВКЛЮЧЕНО',
  DISABLED: 'ИЗКЛЮЧЕНО',
  // refund_status / dispute_status, which the API returns lowercase
  none: 'няма',
  partial: 'частично',
  full: 'пълно',
  won: 'спечелен',
  lost: 'загубен',
  // review queue priority
  high: 'ВИСОК',
  medium: 'СРЕДЕН',
  low: 'НИСЪК',
  // roles
  SUPER_ADMIN: 'ГЛАВЕН АДМИН',
  ADMIN: 'АДМИН',
  MERCHANT_OWNER: 'СОБСТВЕНИК',
  MERCHANT_MEMBER: 'ЧЛЕН НА ЕКИПА',
};

export function badgeLabel(state: string): string {
  return BADGE[state] ?? BADGE[state.toUpperCase()] ?? state.replace(/_/g, ' ');
}

/**
 * Platform settings, keyed by the API's stable `key`.
 *
 * The English label and description still come from the API and are used for
 * anything not listed here, so a setting added on the server shows up in
 * English rather than vanishing.
 */
const SETTINGS: Record<string, { label: string; description: string }> = {
  default_fee_percent_bps: {
    label: 'Такса на платформата по подразбиране',
    description: 'Базисни точки, удържани от всяко плащане при нови търговци. 290 = 2.90%.',
  },
  default_fee_fixed: {
    label: 'Фиксирана такса по подразбиране',
    description: 'Фиксирана сума в стотинки, добавяна към всяко плащане, напр. 30 = $0.30.',
  },
  merchant_signup_enabled: {
    label: 'Регистрация на търговци',
    description: 'Когато е изключено, не могат да се създават нови профили на търговци.',
  },
  payments_enabled: {
    label: 'Приемане на плащания',
    description:
      'Авариен ключ. Когато е изключено, никое ново плащане не може да започне в цялата платформа. Вече започналите плащания се уреждат докрай - за тях Stripe остава източник на истината.',
  },
  payment_link_ttl_seconds: {
    label: 'Валидност на линка за плащане',
    description: 'Колко дълго остава валиден един линк за плащане, в секунди.',
  },
  reconcile_interval_seconds: {
    label: 'Интервал на реконсилиация',
    description: 'Колко често се сверява локалното състояние със Stripe.',
  },
  auto_suspend_on_critical_alert: {
    label: 'Автоматично спиране при критичен сигнал',
    description:
      'Когато е включено, търговецът се спира автоматично, ако реконсилиацията вдигне критичен сигнал срещу негово плащане.',
  },
  dac7_operator_name: {
    label: 'DAC7 - име на оператора',
    description:
      'Юридическото име на отчитащия се оператор на платформа, както трябва да се появи в декларация по DAC7 (Приложение V, Раздел III(B)(1)). Празно, докато някой не реши, че платформата дължи подаване.',
  },
  dac7_operator_address: {
    label: 'DAC7 - адрес на седалището',
    description: 'Адрес на седалището на оператора, на един ред (Приложение V, Раздел III(B)(1)).',
  },
  dac7_operator_tin: {
    label: 'DAC7 - данъчен номер на оператора',
    description:
      'Данъчният идентификационен номер на оператора (Приложение V, Раздел III(B)(1)). За дружество в Уайоминг това е федералният EIN.',
  },
  dac7_registration_member_state: {
    label: 'DAC7 - държава членка на регистрация',
    description:
      'Оператор извън Съюза се регистрира при компетентния орган на ЕДНА държава членка по свой избор (член 8ac(4) и Приложение V, Раздел IV(F)(1)). Двубуквен код на избраната държава; празно, докато няма регистрация.',
  },
  dac7_identification_number: {
    label: 'DAC7 - индивидуален идентификационен номер',
    description:
      'Номерът, издаден от държавата членка на регистрация (Приложение V, Раздел IV(F)(4)). Издава се при регистрацията; празно дотогава.',
  },
  dac7_platform_business_name: {
    label: 'DAC7 - търговско име на платформата',
    description:
      'Търговското име на платформата, за която се отчита (Приложение V, Раздел III(B)(1)). Името, което виждат клиентите и продавачите - не непременно юридическото име на оператора.',
  },
  /**
   * The two facts the payment page states to cardholders about how it is
   * checked. Their descriptions say more than the others here on purpose: these
   * are the only settings in this console whose value is printed, word for
   * word, in front of somebody who is about to type a card number, and whoever
   * edits them should be told that before they type rather than after.
   */
  pci_assessment_standard: {
    label: 'Стандарт за оценка, показван на клиентите',
    description:
      'Стандартът и нивото на самооценка, спрямо които е оценена страницата за плащане, като един кратък текст, напр. „PCI DSS v4.0.1 SAQ A“. Показва се на клиентите в панела за сигурност на страницата за плащане, с моноширинен шрифт. Празно скрива реда, вместо да показва празен.',
  },
  pci_last_passing_scan_date: {
    label: 'Дата на последното успешно външно сканиране',
    description:
      'Датата на последното УСПЕШНО външно сканиране от одобрен от PCI SSC доставчик (ASV), във формат 2026-07-14. Показва се на клиентите до текста, че системите се сканират на всеки три месеца. Оставете празно, докато няма успешен доклад: докато е празно, редът изобщо не се показва. Страницата не публикува и не цитира самия доклад. Бъдеща дата не се приема.',
  },
};

export function settingLabel(key: string, fallback: string): string {
  return SETTINGS[key]?.label ?? fallback;
}

export function settingDescription(key: string, fallback: string): string {
  return SETTINGS[key]?.description ?? fallback;
}

/**
 * Review-queue reasons, keyed by the code the API sends alongside each English
 * sentence. `Stripe requires:` keeps Stripe's own requirement identifiers
 * untranslated on purpose - those strings are what you look up in Stripe's
 * documentation.
 */
const REASONS: Record<string, string> = {
  no_onboarding: 'Не е започнал верификацията в Stripe',
  not_submitted: 'Верификацията е започната, но не е подадена',
  charges_disabled: 'Stripe не е разрешил приемането на плащания',
  payouts_disabled: 'Плащанията са разрешени, но изплащанията - не',
  suspended: 'Спрян - чака преглед',
};

export function reasonLabel(code: string | undefined, fallback: string): string {
  if (code === 'requirements_due') {
    return fallback.replace('Stripe requires:', 'Stripe изисква:');
  }
  return (code && REASONS[code]) ?? fallback;
}

/** Why an action needs a second signature, keyed by `kind:transition`. */
const WHY: Record<string, string> = {
  'setting:update': 'Променя поведението на цялата платформа.',
  // `set_fee` е името на прехода, който наистина се изпраща. `update_pricing`
  // се пази, защото стари записи в опашката за одобрение още го носят.
  'merchant:set_fee': 'Променя колко взима платформата от този търговец при всяко следващо плащане.',
  'merchant:update_pricing': 'Променя колко взима платформата от този търговец.',
  'payment:refund': 'Изнася пари, които не могат да се върнат от наша страна.',
  'merchant:reinstate': 'Връща спрян профил обратно към приемане на плащания.',
  'merchant:suspend': 'Спира приемането на плащания и прекратява сесиите.',
};

export function whyLabel(kind: string, transition: string, fallback: string): string {
  return WHY[`${kind}:${transition}`] ?? fallback;
}

/** Stripe's payment method identifiers, as shown in the revenue breakdown. */
const METHOD: Record<string, string> = {
  card: 'карта',
  link: 'Link',
  sepa_debit: 'SEPA директен дебит',
  bancontact: 'Bancontact',
  ideal: 'iDEAL',
  sofort: 'Sofort',
  giropay: 'giropay',
  p24: 'Przelewy24',
  eps: 'EPS',
  us_bank_account: 'банкова сметка (САЩ)',
  cashapp: 'Cash App Pay',
  klarna: 'Klarna',
  afterpay_clearpay: 'Afterpay / Clearpay',
  unknown: 'неизвестен',
};

export function methodLabel(code: string): string {
  return METHOD[code] ?? code;
}

export interface MoneyByCurrency {
  currency: string;
  amount: number;
}

/**
 * Render a total that may span several currencies.
 *
 * Never adds them. A euro amount and a dollar amount are two facts, not one
 * sum, and joining them with a separator is the only honest way to show both
 * in a single cell.
 */
export function moneyList(totals: MoneyByCurrency[], separator = ' · '): string {
  if (totals.length === 0) return money(0, 'usd');
  return totals.map((t) => money(t.amount, t.currency)).join(separator);
}

/**
 * The largest currency plus a count of the rest, for a headline figure that has
 * room for one number only.
 */
export function moneyHeadline(totals: MoneyByCurrency[]): { value: string; hint: string | null } {
  if (totals.length === 0) return { value: money(0, 'usd'), hint: null };
  const [first, ...rest] = totals;
  return {
    value: money(first.amount, first.currency),
    hint: rest.length > 0 ? `+ ${rest.map((t) => money(t.amount, t.currency)).join(' · ')}` : null,
  };
}
